import { TableStatus } from '@/common/types/phenomTypes';
import { PokerVariant } from '@repo/common-types';
import { GetTableDto, TableFilter } from '@/services/table/table-filters';
import React from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Form, Row, Col } from 'reactstrap';
import 'react-input-range/lib/css/index.css';
import './tables-filter.css';
import InputRangeComponent from './InputRangeComponent';
import { Range } from 'react-input-range';
import { useForm } from 'react-hook-form';
import FormInput from '@/components/forms/FormInput';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';

enum Stakes {
  MICRO = 'Micro',
  MEDIUM = 'Medium',
  HIGH = 'High',
}

interface TablesFilterProps {
  onClose: () => void;
  onApplyFilters: (filters: TableFilter) => void;
  currentFilters: TableFilter;
}

interface FormValues {
  variants: string[];
  minBuyin: number;
  maxBuyin: number;
  minStake: number;
  maxStake: number;
  maxPlayers: number | undefined;
  seatCount: number | undefined;
  waitlist: number | undefined;
  status: TableStatus | undefined;
  isPublic: boolean | undefined;
  stakes: Stakes;
}

const TablesFilter: React.FC<TablesFilterProps> = ({ onClose, onApplyFilters, currentFilters }) => {
  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      variants: currentFilters.variants || [],
      minBuyin: currentFilters.minBuyin || 0,
      maxBuyin: currentFilters.maxBuyin || 100,
      minStake: currentFilters.minStake || 0,
      maxStake: currentFilters.maxStake || 0.2,
      maxPlayers: currentFilters.maxPlayers || undefined,
      seatCount: currentFilters.seatCount || undefined,
      waitlist: currentFilters.waitlist || undefined,
      status: currentFilters.status || undefined,
      isPublic: currentFilters.isPublic || undefined,
      stakes: Stakes.MICRO,
    },
  });

  const [buyInRange, setBuyinRange] = React.useState<Range>({
    min: currentFilters.minBuyin || 0,
    max: currentFilters.maxBuyin || 100,
  });

  const [blindRange, setBlindRange] = React.useState<Range>({
    min: currentFilters.minStake || 0,
    max: currentFilters.maxStake || 0.2,
  });

  const stakes = watch('stakes');
  const minBuyin = watch('minBuyin');
  const maxBuyin = watch('maxBuyin');
  const minStake = watch('minStake');
  const maxStake = watch('maxStake');

  React.useEffect(() => {
    switch (stakes) {
      case Stakes.MICRO:
        setValue('minBuyin', 0);
        setValue('maxBuyin', 100);
        setValue('minStake', 0);
        setValue('maxStake', 0.2);
        setBuyinRange({ min: 0, max: 100 });
        setBlindRange({ min: 0, max: 0.2 });
        break;
      case Stakes.MEDIUM:
        setValue('minBuyin', 100);
        setValue('maxBuyin', 10000);
        setValue('minStake', 0.2);
        setValue('maxStake', 10);
        setBuyinRange({ min: 100, max: 10000 });
        setBlindRange({ min: 0.2, max: 10 });
        break;
      case Stakes.HIGH:
        setValue('minBuyin', 10000);
        setValue('maxBuyin', 200000);
        setValue('minStake', 10);
        setValue('maxStake', 100000);
        setBuyinRange({ min: 10000, max: 200000 });
        setBlindRange({ min: 10, max: 100000 });
        break;
    }
  }, [stakes, setValue]);

  const handleBuyinChange = (value: Range | number) => {
    if (typeof value === 'number') {
      // setBuyinRange({ min: value, max: value });
      setValue('minBuyin', value);
      setValue('maxBuyin', value);
    } else {
      // setBuyinRange(value);
      setValue('minBuyin', value.min);
      setValue('maxBuyin', value.max);
    }
  };

  const handleBlindChange = (value: Range | number) => {
    if (typeof value === 'number') {
      // setBlindRange({ min: value, max: value });
      setValue('minStake', value);
      setValue('maxStake', value);
    } else {
      // setBlindRange(value);
      setValue('minStake', value.min);
      setValue('maxStake', value.max);
    }
  };

  const onSubmit = (data: FormValues) => {
    const { stakes, ...rest } = data;
    const cleanedFilters = Object.entries(rest).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value) || typeof value === 'number' || typeof value === 'boolean') {
          acc[key] = value;
        } else if (typeof value === 'string') {
          acc[key] = value;
        }
      }
      return acc;
    }, {} as TableFilter);

    onApplyFilters(cleanedFilters);
  };

  return (
    <Modal isOpen centered>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={onClose} className="bg-dark p-3">
          Filter Tables
        </ModalHeader>

        <ModalBody className="p-4">
          <Row className="g-3">
            <Col xs={12}>
              <FormInput
                label="Variants"
                name="variants"
                control={control}
                type="select"
                options={[
                  ...Object.values(PokerVariant).map((variant) => ({
                    label: variant,
                    value: variant,
                  })),
                ]}
              />
            </Col>

            <Col xs={12}>
              <h6>Stakes</h6>
              <div className="d-flex gap-2">
                {Object.values(Stakes).map((stake) => {
                  return (
                    <Button
                      key={stake}
                      type="button"
                      variant={stakes === stake ? ButtonVariants.primary : ButtonVariants.secondary}
                      size={ButtonSizes.sm}
                      onClick={() => setValue('stakes', stake)}
                      className={'btn' + (stakes === stake ? ' btn-primary' : ' btn-dark')}
                    >
                      {stake}
                    </Button>
                  );
                })}
              </div>
            </Col>

            <Col xs={12}>
              <h6>Buy in Range</h6>
              <InputRangeComponent
                minValue={buyInRange.min}
                maxValue={buyInRange.max}
                value={{ min: minBuyin, max: maxBuyin }}
                onChange={handleBuyinChange}
              />
            </Col>

            <Col xs={12}>
              <h6>Blind Range</h6>
              <InputRangeComponent
                minValue={blindRange.min}
                maxValue={blindRange.max}
                value={{ min: minStake, max: maxStake }}
                onChange={handleBlindChange}
              />
            </Col>

            <Col xs={6}>
              <FormInput
                label="Max Players"
                name="maxPlayers"
                control={control}
                type="select"
                options={[
                  ...Array.from({ length: 8 }, (_, i) => i + 2).map((value) => ({
                    label: value.toString(),
                    value,
                  })),
                ]}
              />
            </Col>

            <Col xs={6}>
              <FormInput
                label="Seat Count"
                name="seatCount"
                control={control}
                type="select"
                options={[
                  ...Array.from({ length: 8 }, (_, i) => i + 2).map((value) => ({
                    label: value.toString(),
                    value,
                  })),
                ]}
              />
            </Col>

            <Col xs={6}>
              <FormInput
                label="Waitlist"
                name="waitlist"
                control={control}
                type="select"
                options={[
                  ...Array.from({ length: 10 }, (_, i) => i).map((value) => ({
                    label: value.toString(),
                    value,
                  })),
                ]}
              />
            </Col>

            <Col xs={6}>
              <FormInput
                label="Status"
                name="status"
                control={control}
                type="select"
                options={[
                  ...Object.values(TableStatus).map((status) => ({
                    label: status,
                    value: status,
                  })),
                ]}
              />
            </Col>

            <Col xs={12}>
              <FormInput
                label="Public"
                name="isPublic"
                control={control}
                type="select"
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
              />
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button
            className="btn btn-outline"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            onClick={handleSubmit(onSubmit)}
          >
            Apply Filter
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default TablesFilter;
